import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../redux/ReduxHooks';
import { isProjectViewPathname } from '../../utils/helpers';
import { useMeasuredBottomSnackbarStackIndex } from './SnackbarStackContext';
import { bottomCenterSnackbarStackSx, defaultSnackbarContentSx } from './snackbarStyles';

interface ViewerReadOnlyModeSnackbarProps {
  stackIndex?: number;
}

const ViewerReadOnlyModeSnackbar = ({ stackIndex: stackIndexOverride }: ViewerReadOnlyModeSnackbarProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const projectId = useAppSelector((state) => state.project.loadedProject?.id);
  const accessLevel = useAppSelector((state) => state.project.loadedProject?.accessLevel);
  const [dismissed, setDismissed] = useState(false);

  const isProjectViewPage = useMemo(() => isProjectViewPathname(pathname), [pathname]);

  const shouldShow = Boolean(projectId && accessLevel === 'viewer' && isProjectViewPage && !dismissed);
  const { ref: snackbarRef, stackIndex: registeredStackIndex } = useMeasuredBottomSnackbarStackIndex<HTMLDivElement>({
    disabled: stackIndexOverride !== undefined,
    horizontal: 'center',
    open: shouldShow,
  });
  const stackIndex = stackIndexOverride ?? registeredStackIndex;

  useEffect(() => {
    setDismissed(false);
  }, [projectId]);

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleSnackbarClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway' || reason === 'escapeKeyDown') {
      return;
    }
    handleDismiss();
  };

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      ref={snackbarRef}
      open={shouldShow}
      onClose={handleSnackbarClose}
      message={t('primaryBar.text.read-only-mode', 'You are in read-only mode. See control options on the bottom bar.')}
      action={
        <IconButton size="small" aria-label={t('aria-label.close', 'close')} color="inherit" onClick={handleDismiss}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
      slotProps={{
        content: {
          sx: defaultSnackbarContentSx,
        },
      }}
      sx={bottomCenterSnackbarStackSx(stackIndex)}
    />
  );
};

export default ViewerReadOnlyModeSnackbar;
